/**
 *
 * MessageInput
 *
 */

import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isColorDark from 'utils/isColorDark';
import chroma from 'chroma-js';
import FileSharing from "../FileSharing";
import { useDropzone } from "react-dropzone";
import {addFilesToUppy, convertFileToDataURI, uploadFilesToTransloadit} from "utils/uppy";
import DragAndDrop from "../DragAndDrop";
import {MESSAGE_TYPES, MAX_FILE_SIZE} from "../../containers/App/constants";
import {fileValidate} from "../../../utils/fileValidate";
import Gif from '../../components/Gif'
import {useConfig} from "context";
const RestartIcon = styled(({ textColor }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.5699 2.43462C12.9111 1.77664 11.524 1.04884 10.9281 0.752266C10.797 0.687049 10.6462 0.783104 10.6462 0.92948V2.61823C10.6462 2.79542 10.74 2.95925 10.8903 3.05313C11.2149 3.25591 11.7819 3.62743 12.0764 3.92272C14.2132 6.06052 14.2132 9.53633 12.0764 11.6731C9.93971 13.8066 6.46605 13.8066 4.3261 11.6731C2.19152 9.53419 2.19152 6.06052 4.3261 3.92272C4.33146 3.91629 4.34862 3.90556 4.35612 3.89592L5.4534 4.98948C5.51651 5.05237 5.62415 5.00756 5.62399 4.91846L5.61731 1.34529C5.6171 1.23509 5.52778 1.14581 5.41757 1.14567L1.84695 1.1409C1.75779 1.14078 1.71306 1.24856 1.7761 1.31161L2.86588 2.40138C2.85837 2.41425 2.84658 2.42711 2.83693 2.43462C-0.125331 5.39581 -0.125331 10.2 2.83693 13.1612C5.80348 16.1256 10.6044 16.1256 13.5699 13.1612C16.53 10.2 16.5332 5.39581 13.5699 2.43462Z"
      fill={textColor}
    />
  </svg>
))`
  width: 15.18px;
  height: 14.77px;
  margin: 0;
`;

const SendIcon = styled(({ textColor }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1.23402 0.0924218C0.682503 -0.185081 0.0175781 0.195829 0.0175781 0.788806V4.00309V5.57896V5.99101C0.0175781 6.44843 0.378216 6.83313 0.856094 6.88558L7.6772 7.63393C8.12572 7.68312 8.12572 8.30248 7.6772 8.3517L0.856094 9.10005C0.378216 9.15247 0.0175781 9.53721 0.0175781 9.99462V12.0076V13.5834V15.328C0.0175781 15.7445 0.372139 16.0346 0.807359 15.9871C1.24357 15.9394 -0.448382 16.7401 15.5453 8.6892C16.1323 8.39365 16.1326 7.59199 15.5453 7.2964L1.23402 0.0924218Z"
      fill={textColor}
    />
  </svg>
))`
  width: 15.18px;
  height: 14.77px;
  align-items: center;
  justify-content: center;
  /* display: flex; */
  margin: 0;
`;

const MessageInputWrapper = styled.form`
  position: absolute;
  bottom: 0;
  width: 100%;
  min-height: 80px;
  background: ${p => p.bgColor};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: -1;
  padding: 40px 20px 3px 20px;
  box-sizing: border-box;
  border-bottom-left-radius: 20px;
  margin-bottom: 0 !important;
  border-bottom-right-radius: 20px;
`;

const InputWrapper = styled.form`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-block-end: 1rem;
  width: inherit;
  margin-bottom: 0;
  background: ${p => p.bgColor};
  input,
  textarea {
    cursor: text;
    border: none;
    line-height: 20px;
    width: 100%;
    resize: none;
    padding-right: 8px;
    background: transparent;
    overflow: overlay;
    height: 24px;
    color: ${p => p.textColor};
    font-size: 14px;
    font-family: 'Rubik', serif;

    &:focus {
      outline: unset;
    }

    ::placeholder {
      opacity: 0.5;
      color: ${p => p.textColor};
    }
    ::-webkit-scrollbar {
      width: 4px !important;
    }
    ::-webkit-scrollbar-thumb {
      background-color: ${p =>
        p.bgColor? (chroma(p.bgColor)
          .brighten(0.5)
          .hex()) : ''};
      border-radius: 30px !important;
    }
  }
  img {
    z-index: 100;
  }
  button {
    border: none;
    padding: 0;
    font: inherit;
    cursor: pointer;
    outline: inherit;
    background: transparent;
  }
`;

const ButtonWrapper = styled.button`
  cursor: pointer;
  background: ${p => p.bgColor};
  border: none;
  margin-bottom: 12px;
  color: ${p => p.textColor};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Rubik', serif;
  transition: 0.2s all;
  &:active,
  &:focus {
    outline: none;
  }
  &:hover {
    span {
      opacity: 0.8;
    }
  }

  img {
    margin-right: 10px;
    margin-top: -2px;
    height: 17px;
    width: 17px;
  }
  span {
    opacity: 0.5;
  }
`;

const BotIcon = styled.button`
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
  height: 24px;
  width: 24px;
  border-radius: 50px;
  margin: ${p => p.margin};

  &&& {
    background: ${p =>
      p.isColorDark ? p.theme.palette.white + '33' : p.theme.palette.black + '33'};
  }
`;

const InputButtonWrapper = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: inherit;
  margin-bottom: 8px;
  background: ${p => p.bgColor};
  cursor: pointer;
  background: ${p => p.bgColor};
  border: none;
  color: ${p => p.textColor};
  font-size: 14px;
  height: 24px;
  &:active,
  &:focus {
    outline: none;
  }
  &:hover {
    p {
      opacity: 0.8;
    }
  }

  p {
    opacity: 0.6;
    font-size: 14px;
    line-height: 17px;
    margin: 0;
    align-items: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 260px;
  }
`;

const FooterDiv = styled.div`
  background: ${p => p.bgColor};
`;

const InputActions = styled.div`
  flex-direction: row;
  display: flex;
  align-items: center;
  justify-content: space-between;
  align-self: flex-end;
`;

const TextAreaWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-right: 8px;
  margin-bottom: -4px;
  ::before {
    content: '';
    position: absolute;
    top: 0;
    height: 6px;
    width: 98%;
    background-image: ${p =>
      p.bgColor? (`linear-gradient(180deg, ${p.bgColor} 0%, rgba(${chroma(p.bgColor)
        .alpha(0.8)
        .rgba()}) 82.81%, rgba(${chroma(p.bgColor)
        .alpha(0)
        .rgba()}) 100%)`) : ''};
  };
  align-self: flex-end;
`;

export const MessageInput = (props) => {
  const {
    placeholder,
    bgColor,
    Language,
    textColor,
    inputButtonClick,
    type,
    isServiceCloud,
    onSubmit,
    triggerBot,
    fieldType,
    showAttachFiles,
    botHover,
    showGif,
    textAreaNumberOfLines,
    setTextAreaNumberOfLinesStart,
    handleSendingMessageToPusher,
    handleSendingGifsToPusher,
    pushMessage,
    setProgressBarCompletion,
    removeFromConversation,
    setUploadErrorMessage,
    setPreviewRetry
  } = props;
  const { config } = useConfig();
  const [textAreaValue, setTextAreaValue] = useState('');
  const textAreaRef = useRef();
  const inputButtonRef = useRef();
  const [textAreaCurrentHeight, setTextAreaCurrentHeight] = useState(24);

  const onDrop = async (acceptedFiles) => {
    const files = Array.from(acceptedFiles);
    let fileUploadError = fileValidate(files[0], Language);
    if(fileUploadError !==''){
      setUploadErrorMessage(fileUploadError);
      return;
    }
    setProgressBarCompletion(0.1);
    setUploadErrorMessage('');
    const url = await convertFileToDataURI(files[0]);
    let id = Math.random().toString(16).slice(2);
    const message = {
      img: null,
      name: null,
      lead: true,
      attachments: {
        url,
        type: `${files[0].name.split('.').pop()}`,
        name: files[0].name,
        size: files[0].size,
      },
      time: Date.now(),
      type: MESSAGE_TYPES.attachments,
      preview: true,
      id,
    };
    pushMessage({ meta: { message } });
    addFilesToUppy([files[0]], setProgressBarCompletion, id, removeFromConversation, setUploadErrorMessage);
    uploadFilesToTransloadit({sendToPusher: meta => handleSendingMessageToPusher(meta, id)}, config, setProgressBarCompletion, setUploadErrorMessage, setPreviewRetry);
  }

  const handleTriggerBot = e => {
    e.preventDefault();
    setTextAreaValue('');
    triggerBot();
  };

  const handleSubmit = e => {
    e.preventDefault();
    setTextAreaNumberOfLinesStart(1);
    setTextAreaValue('');
    textAreaRef.current.style.height = '24px';
    if (textAreaValue && textAreaValue.trim().length > 0) onSubmit(e);
  };

  const handleUserKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      try {
        inputButtonRef.current.click();
      } catch (e) {}
    }
  };

  const getColor = (props) => {
    if (props.isDragAccept) {
      return '#00e676';
    }
    if (props.isDragReject) {
      return '#ff1744';
    }
    if (props.isFocused) {
      return '#2196f3';
    }
    return '#eeeeee';
  }

  const handleChange = e => {
    if(fieldType === "number"){
      const regex = /^[0-9\b]+$/;
      if (e.target.value === '' || regex.test(e.target.value)) {
        setTextAreaValue(e.target.value);
      }
    }
    else setTextAreaValue(e.target.value);
    e.target.style.height = '24px';
    const rows = Math.ceil((e.target.scrollHeight - 24) / 20);
    setTextAreaNumberOfLinesStart(rows + 1);
    e.target.style.height = rows < 5 ? `${24 + rows * 20}px` : '102px';
    setTextAreaCurrentHeight(rows < 5 ? 24 + rows * 20 : 102);
    inputButtonRef.current.disabled = !((e.target.value && e.target.value.trim().length >= 2 && isServiceCloud) || !isServiceCloud);
  };

  const validate = file => {

  }

  const handlePaste = async e => {
    if(e && e.clipboardData && e.clipboardData.files && e.clipboardData.files.length > 0){

      const files = e.clipboardData.files;
      let fileUploadError = fileValidate(files[0], Language);
      if(fileUploadError !==''){
        setUploadErrorMessage(fileUploadError);
        return;
      }
      setProgressBarCompletion(0.1);
      setUploadErrorMessage('');
      const url = await convertFileToDataURI(files[0]);
      let id = Math.random().toString(16).slice(2);
      const message = {
        img: null,
        name: null,
        lead: true,
        attachments: {
          url,
          type: `${files[0].name.split('.').pop()}`,
          name: files[0].name,
          size: files[0].size,
        },
        time: Date.now(),
        type: MESSAGE_TYPES.attachments,
        preview: true,
        id,
      };
      pushMessage({ meta: { message } });
      addFilesToUppy([files[0]], setProgressBarCompletion, id, removeFromConversation, setUploadErrorMessage);
      uploadFilesToTransloadit({sendToPusher: meta => handleSendingMessageToPusher(meta, id)},config,  setProgressBarCompletion, setUploadErrorMessage, setPreviewRetry);
    }
  }

  const setHeight = () => {
    if(!textAreaRef || !textAreaRef.current) return;
    textAreaRef.current.style.height = textAreaCurrentHeight;
  }
 
  return (
    <MessageInputWrapper
        id="insent-message-input-message-input-wrapper"
      bgColor={bgColor}
      textAreaNumberOfLines={textAreaNumberOfLines}
    >
      {type === 'button' && (
          <ButtonWrapper id="insent-message-input-buttons-wrapper" bgColor={bgColor} textColor={textColor} onClick={(e) => handleTriggerBot(e)}>
            <BotIcon id="insent-message-input-bot-icon" isColorDark={isColorDark(bgColor)} margin="0 10px 0 0">
              <RestartIcon id="insent-message-input-restart-icon" textColor={textColor} />
            </BotIcon>
            <span>{Language && Language.RestartConversation}</span>
          </ButtonWrapper>
      )}
      {(type === 'input' && showAttachFiles) && (
          <DragAndDrop onDrop={onDrop} textAreaNumberOfLines={textAreaNumberOfLines} setHeight={setHeight}>
            <InputWrapper
                id="insent-message-input-input-wrapper"
                bgColor={bgColor}
                textColor={textColor}
                onSubmit={handleSubmit}
                increaseCoefficient={textAreaNumberOfLines - 1}
            >
                <TextAreaWrapper id="insent-message-input-text-area-wrapper" bgColor={bgColor}>
                <textarea
                    name="message"
                    autoFocus
                    placeholder={placeholder}
                    rows="1"
                    onKeyDown={handleUserKeyPress}
                    id="textInputArea"
                    onChange={handleChange}
                    onPaste={handlePaste}
                    ref={textAreaRef}
                    value={textAreaValue}
                />
                </TextAreaWrapper>
                <InputActions id="insent-message-input-input-actions">
                  <FileSharing Language={Language} setPreviewRetry={setPreviewRetry} setUploadErrorMessage={setUploadErrorMessage} removeFromConversation={removeFromConversation} setProgressBarCompletion={setProgressBarCompletion} handleSendingMessageToPusher={handleSendingMessageToPusher} pushMessage={pushMessage}/>
                  {showGif && <Gif textColor={textColor} pushMessage={pushMessage} handleSendingMessageToPusher={handleSendingGifsToPusher}/>}
                  <button ref={inputButtonRef} disabled={true}>
                    <SendIcon textColor={textColor} />
                  </button>
                </InputActions>
            </InputWrapper>
          </DragAndDrop>
      )}
      {(type === 'input' && !showAttachFiles) && (
          <InputWrapper
              id="insent-message-input-input-wrapper"
              bgColor={bgColor}
              textColor={textColor}
              onSubmit={handleSubmit}
              increaseCoefficient={textAreaNumberOfLines - 1}
          >
            <TextAreaWrapper id="insent-message-input-text-area-wrapper" bgColor={bgColor}>
            <textarea
                name="message"
                autoFocus
                placeholder={placeholder}
                rows="1"
                onKeyDown={handleUserKeyPress}
                id="textInputArea"
                onChange={handleChange}
                ref={textAreaRef}
                value={textAreaValue}
            />
            </TextAreaWrapper>
            {showGif && <Gif textColor={textColor} pushMessage={pushMessage} handleSendingMessageToPusher={handleSendingGifsToPusher}/>}
            <InputActions id="insent-message-input-input-actions">
              <button type="submit" ref={inputButtonRef} disabled={true}>
                <SendIcon textColor={textColor} />
              </button>
            </InputActions>
          </InputWrapper>
      )}
      {type === 'inputButton' && (
        <InputButtonWrapper id="insent-message-input-input-button-wrapper" bgColor={bgColor} textColor={textColor} onClick={inputButtonClick}>
          <p>{placeholder}</p>
          <SendIcon id="insent-message-input-input-button-wrapper-send-icon" textColor={textColor} />
        </InputButtonWrapper>
      )}
      {type === 'footer' && <FooterDiv bgColor={bgColor}></FooterDiv>}
    </MessageInputWrapper>
  );
};

MessageInput.propTypes = {
  placeholder: PropTypes.string,
  bgColor: PropTypes.string,
  textColor: PropTypes.string,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['input', 'button', 'footer', 'inputButton']),
  showAttachFiles: PropTypes.bool,
};

export default MessageInput;
